import { intervalToDuration } from 'date-fns'
import { useMemo } from 'react'
import { NoData } from 'src/components/analytics/common/NoData'
import { TrendIndicator } from 'src/components/analytics/common/TrendIndicator'
import BreakdownTable, { BreakdownItem } from 'src/components/analytics/tables/BreakdownTable'
import { useChartData } from 'src/components/overview/useChartData'
import { useAnalytics } from 'src/lib/contexts/AnalyticsContext'
import { WidgetId } from 'src/lib/contexts/AnalyticsContext/types'
import { percentageWithFixedDecmials, formatNumberToTwoDecimals } from 'src/lib/utils/analytics'
import { ChartSkeleton } from '../common/Skeleton'

const ABANDONED_CHECKOUTS_RECOVERY_KEYS = {
  recovered_gmv: 'Recovered GMV',
  average_time_difference: 'Avg recovery time',
  recovered_orders: 'Total abandoned checkouts recovered',
  abandoned_checkout_conversion: '% of abandoned checkouts recovered',
  abandoned_checkouts: 'Abandoned checkouts' // for internal use
}

// Helper to format seconds to 'Xd Xh Xm' using date-fns
export function formatDuration(inputSeconds: number) {
  const duration = intervalToDuration({ start: 0, end: inputSeconds * 1000 })
  const { days = 0, hours = 0, minutes = 0, seconds = 0 } = duration
  let str = ''
  if (days > 0) str += `${days}d `
  if (hours > 0) str += `${hours}h `
  if (minutes > 0) str += `${minutes}m `

  if (inputSeconds > 60 * 60 * 24) {
    return str.trim()
  }

  if (seconds > 0) str += `${seconds}s`
  return str.trim()
}

const AbandonedCheckoutsRecoveryTable = () => {
  const {
    getters: { refreshKey }
  } = useAnalytics()
  const { ref, isLoading, isError, data } = useChartData({
    chartConfig: {
      id: WidgetId.orders_abandoned_checkout_recovery,
      dependencies: {
        endpoints: ['/abandoned-checkout']
      }
    },
    refreshKey
  })
  const chartBreakdownData: BreakdownItem[] = useMemo(() => {
    const rawData = data[0]?.data?.result?.widgetData?.data
    const comparedData = data[0]?.data?.result?.widgetData?.compared_data

    // Build a lookup for easy access
    const rawLookup = Object.fromEntries(
      rawData?.map((item: { dimension: string; data: { x: number }[] }) => [item.dimension, item.data[0].x]) || []
    )
    const comparedLookup = Object.fromEntries(
      comparedData?.map((item: { dimension: string; data: { x: number }[] }) => [item.dimension, item.data[0].x]) || []
    )

    // Compose the rows in the order shown in the UI
    const rows = [
      {
        label: ABANDONED_CHECKOUTS_RECOVERY_KEYS.recovered_gmv,
        value: Number(rawLookup.recovered_gmv || 0).toLocaleString('en-IN', {
          maximumFractionDigits: 2,
          style: 'currency',
          currency: 'INR'
        }),
        change: percentageWithFixedDecmials(
          formatNumberToTwoDecimals(rawLookup.recovered_gmv || 0),
          formatNumberToTwoDecimals(comparedLookup.recovered_gmv || 0)
        ),
        bold: true
      },
      {
        label: ABANDONED_CHECKOUTS_RECOVERY_KEYS.average_time_difference,
        value: formatDuration(Number(rawLookup.average_time_difference || 0)),
        change: percentageWithFixedDecmials(
          formatNumberToTwoDecimals(rawLookup.average_time_difference || 0),
          formatNumberToTwoDecimals(comparedLookup.average_time_difference || 0)
        ),
        bold: false,
        reverseComparison: true
      },
      {
        label: ABANDONED_CHECKOUTS_RECOVERY_KEYS.recovered_orders,
        value: `${rawLookup.recovered_orders || 0} of ${rawLookup.abandoned_checkouts || 0}`,
        change: percentageWithFixedDecmials(
          formatNumberToTwoDecimals(rawLookup.recovered_orders || 0),
          formatNumberToTwoDecimals(comparedLookup.recovered_orders || 0)
        ),
        bold: false
      },
      {
        label: ABANDONED_CHECKOUTS_RECOVERY_KEYS.abandoned_checkout_conversion,
        value: `${Number(rawLookup.abandoned_checkout_conversion || 0).toFixed(2)}%`,
        change: percentageWithFixedDecmials(
          formatNumberToTwoDecimals(rawLookup.abandoned_checkout_conversion || 0),
          formatNumberToTwoDecimals(comparedLookup.abandoned_checkout_conversion || 0)
        ),
        bold: false
      }
    ]
    return rows
  }, [data])

  const renderComponent = () => {
    if (isLoading) {
      return <ChartSkeleton />
    } else if (isError) {
      return <div>Error</div>
    } else if (chartBreakdownData.length > 0) {
      return (
        <div className='flex flex-col h-full'>
          <div className='text-sm'>
            <div className='flex items-center gap-2 pt-4'>
              <span className='font-bold text-[18px]'>{chartBreakdownData[0].value}</span>
              <span className='font-bold text-[18px]'>
                <TrendIndicator value={chartBreakdownData[0].change} formatter={value => `${Math.abs(value)}%`} />
              </span>
            </div>
            <p className='text-coal-slate text-xs py-2'>{chartBreakdownData[0].label}</p>
          </div>
          <div className='flex-1 pt-4'>
            <BreakdownTable data={chartBreakdownData.slice(1, chartBreakdownData.length)} />
          </div>
        </div>
      )
    } else {
      return <NoData />
    }
  }

  return (
    <div ref={ref} className='w-full h-full'>
      {renderComponent()}
    </div>
  )
}

export default AbandonedCheckoutsRecoveryTable
